import React, { useState, useEffect } from 'react';
import { gastosAPI, obrasAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Wallet } from 'lucide-react';
import { formatCurrency, formatDate, translateCategoria, getTodayISO } from '../lib/utils';

const GastosPage = () => {
  const [gastos, setGastos] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterObra, setFilterObra] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGasto, setSelectedGasto] = useState(null);
  const [formData, setFormData] = useState({
    obra_id: '',
    proveedor: '',
    concepto: '',
    importe: 0,
    fecha: getTodayISO(),
    categoria: 'material',
    factura_proveedor: '',
    notas: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [gastosRes, obrasRes] = await Promise.all([
        gastosAPI.list(),
        obrasAPI.list(),
      ]);
      setGastos(gastosRes.data);
      setObras(obrasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedGasto) {
        await gastosAPI.update(selectedGasto.id, formData);
        toast.success('Gasto actualizado');
      } else {
        await gastosAPI.create(formData);
        toast.success('Gasto registrado');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar gasto');
    }
  };

  const handleEdit = (gasto) => {
    setSelectedGasto(gasto);
    setFormData({
      obra_id: gasto.obra_id || '',
      proveedor: gasto.proveedor || '',
      concepto: gasto.concepto || '',
      importe: gasto.importe || 0,
      fecha: gasto.fecha || getTodayISO(),
      categoria: gasto.categoria || 'material',
      factura_proveedor: gasto.factura_proveedor || '',
      notas: gasto.notas || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await gastosAPI.delete(selectedGasto.id);
      toast.success('Gasto eliminado');
      setDeleteDialogOpen(false);
      setSelectedGasto(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar gasto');
    }
  };

  const resetForm = () => {
    setSelectedGasto(null);
    setFormData({
      obra_id: '',
      proveedor: '',
      concepto: '',
      importe: 0,
      fecha: getTodayISO(),
      categoria: 'material',
      factura_proveedor: '',
      notas: '',
    });
  };

  const getObraName = (obraId) => {
    const obra = obras.find((o) => o.id === obraId);
    return obra ? `${obra.numero} - ${obra.titulo}` : 'Sin obra';
  };

  const filteredGastos = gastos.filter((g) => {
    const matchSearch = g.concepto?.toLowerCase().includes(search.toLowerCase()) ||
      g.proveedor?.toLowerCase().includes(search.toLowerCase());
    const matchObra = !filterObra || g.obra_id === filterObra;
    return matchSearch && matchObra;
  });

  const totalGastos = filteredGastos.reduce((sum, g) => sum + (g.importe || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6 animate-fade-in" data-testid="gastos-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Gastos</h1>
          <p className="text-slate-500 text-sm mt-1">
            {gastos.length} gastos · Total: {formatCurrency(totalGastos)}
          </p>
        </div>
        <div className="lg:hidden flex-1">
          <p className="text-xs text-slate-500">{gastos.length} gastos</p>
          <p className="text-sm font-bold text-red-600 font-mono">-{formatCurrency(totalGastos)}</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-slate-900 hover:bg-slate-800 h-10 lg:h-9 active:scale-95 transition-transform"
          data-testid="new-gasto-btn"
        >
          <Plus className="w-4 h-4 mr-1 lg:mr-2" />
          <span className="hidden sm:inline">Nuevo Gasto</span>
          <span className="sm:hidden">Nuevo</span>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 lg:flex-row lg:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar gastos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12 lg:h-10 lg:max-w-md"
            data-testid="search-gastos-input"
          />
        </div>
        <Select value={filterObra || "all"} onValueChange={(v) => setFilterObra(v === "all" ? "" : v)}>
          <SelectTrigger className="h-12 lg:h-10 lg:w-[250px]" data-testid="filter-obra-select">
            <SelectValue placeholder="Filtrar por obra" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las obras</SelectItem>
            {obras.map((o) => (
              <SelectItem key={o.id} value={o.id}>{o.numero} - {o.titulo}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {filteredGastos.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Wallet className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">{search || filterObra ? 'No se encontraron gastos' : 'No hay gastos'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="lg:hidden space-y-3">
            {filteredGastos.map((gasto) => (
              <Card 
                key={gasto.id} 
                className="border-slate-200 active:scale-[0.99] transition-transform"
                data-testid={`gasto-card-${gasto.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-slate-100 text-slate-600">
                          {translateCategoria(gasto.categoria)}
                        </span>
                        <span className="text-[10px] text-slate-400">{formatDate(gasto.fecha)}</span>
                      </div>
                      <p className="font-semibold text-slate-900 truncate">{gasto.concepto}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{gasto.proveedor}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="text-base font-bold text-red-600 font-mono">-{formatCurrency(gasto.importe)}</p>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(gasto)}
                          data-testid={`edit-gasto-${gasto.id}`}
                        >
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => { setSelectedGasto(gasto); setDeleteDialogOpen(true); }}
                          data-testid={`delete-gasto-${gasto.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-slate-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {gasto.obra_id && (
                    <p className="text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-100 truncate">
                      📍 {getObraName(gasto.obra_id)}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
            {/* Mobile Total */}
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-4 flex justify-between items-center">
                <span className="font-medium text-slate-600">Total Gastos</span>
                <span className="text-lg font-bold text-red-600 font-mono">-{formatCurrency(totalGastos)}</span>
              </CardContent>
            </Card>
          </div>

          {/* Desktop Table View */}
          <Card className="border-slate-200 hidden lg:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Fecha</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Concepto</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Proveedor</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Obra</th>
                      <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Categoría</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Importe</th>
                      <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredGastos.map((gasto) => (
                      <tr key={gasto.id} className="hover:bg-slate-50" data-testid={`gasto-row-${gasto.id}`}>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {formatDate(gasto.fecha)}
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{gasto.concepto}</p>
                          {gasto.factura_proveedor && (
                            <p className="text-xs text-slate-500">Fact: {gasto.factura_proveedor}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{gasto.proveedor}</td>
                        <td className="px-4 py-3 text-sm text-slate-500 max-w-xs truncate">
                          {getObraName(gasto.obra_id)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-600">
                            {translateCategoria(gasto.categoria)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm font-medium text-red-600">
                          -{formatCurrency(gasto.importe)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEdit(gasto)}
                            >
                              <Pencil className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => { setSelectedGasto(gasto); setDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="w-4 h-4 text-slate-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 border-t border-slate-200">
                      <td colSpan={5} className="px-4 py-3 text-right font-medium text-slate-600">Total:</td>
                      <td className="px-4 py-3 text-right font-mono font-bold text-red-600">
                        -{formatCurrency(totalGastos)}
                      </td>
                      <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="gasto-dialog">
          <DialogHeader>
            <DialogTitle>{selectedGasto ? 'Editar Gasto' : 'Nuevo Gasto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Obra *</Label>
              <Select
                value={formData.obra_id}
                onValueChange={(value) => setFormData({ ...formData, obra_id: value })}
                required
              >
                <SelectTrigger data-testid="gasto-obra-select">
                  <SelectValue placeholder="Seleccionar obra" />
                </SelectTrigger>
                <SelectContent>
                  {obras.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.numero} - {o.titulo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Proveedor *</Label>
                <Input
                  value={formData.proveedor}
                  onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })}
                  placeholder="Nombre proveedor"
                  required
                  data-testid="gasto-proveedor-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                >
                  <SelectTrigger data-testid="gasto-categoria-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="material">Material</SelectItem>
                    <SelectItem value="mano_obra">Mano de Obra</SelectItem>
                    <SelectItem value="transporte">Transporte</SelectItem>
                    <SelectItem value="otros">Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Concepto *</Label>
              <Input
                value={formData.concepto}
                onChange={(e) => setFormData({ ...formData, concepto: e.target.value })}
                placeholder="Descripción del gasto"
                required
                data-testid="gasto-concepto-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importe (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.importe}
                  onChange={(e) => setFormData({ ...formData, importe: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="gasto-importe-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  required
                  data-testid="gasto-fecha-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nº Factura Proveedor</Label>
              <Input
                value={formData.factura_proveedor}
                onChange={(e) => setFormData({ ...formData, factura_proveedor: e.target.value })}
                placeholder="Referencia factura"
                data-testid="gasto-factura-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Notas adicionales..."
                rows={2}
                data-testid="gasto-notas-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-gasto-btn">
                {selectedGasto ? 'Guardar' : 'Registrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el gasto "{selectedGasto?.concepto}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-gasto-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GastosPage;
